import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';

import {
    editField
} from '../redux/slice';

//Icons
import { Icon } from '@mui/material';

//Helpers
import { formatCatalogAddress } from 'Core/data/Helpers';
import { endpoints } from 'Core/defaultValues';
import { useSupportChatAvailable } from 'Modules/.common/hooks/useSupportChatAvailable';

//Components
import { RadioGroup, Button, IconButton, Tooltip } from '@gpstrackit/gtc-react-controls'
import Loading from './Loading';
import Map from './Map'

const VerifyLocation = ({ resetDevice, openChat }) => {
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const supportChatAvailable = useSupportChatAvailable();

    //Redux States
    const {
        rebootVidfleetDevice,
        loadingRebootVidfleetDevice,
        loadingRebootGeneralDevice,
        loadingSendPing,
        rebootGeneralDevice,
        loadingLastReading,
        locationValidation,
        refreshLocation,
        confirmLocation,
        lastReadings,
        unitDetail,
        isLocation,
        confirmLastReading
    } = useSelector(state => state.troubleshootDeviceRedux);

    //Local states
    const [address, setAddress] = useState(null);
    const [hDop, setHDop] = useState(null);
    const [data, setData] = useState({});
    const [openConfirmReset, setOpenConfirmReset] = useState(false);

    //Options for RadioGroup
    const options = [
        { label: messages['troubleshootDevice_yes'], value: "1" },
        { label: messages['troubleshootDevice_no'], value: "2" }
    ];

    const hDopLabel = (value) => {
        // Objeto para mapear los rangos de valor a colores y etiquetas
        const labels = {
            1: { color: 'red', label: messages['troubleshootDevice_bad'] },
            2: { color: 'red', label: messages['troubleshootDevice_bad'] },
            3: { color: 'red', label: messages['troubleshootDevice_bad'] },
            4: { color: 'red', label: messages['troubleshootDevice_bad'] },
            5: { color: 'yellow', label: messages['troubleshootDevice_poor'] },
            6: { color: 'yellow', label: messages['troubleshootDevice_poor'] },
            7: { color: 'yellow', label: messages['troubleshootDevice_poor'] },
            8: { color: 'yellow', label: messages['troubleshootDevice_poor'] },
            9: { color: 'green', label: messages['troubleshootDevice_good'] },
            10: { color: 'green', label: messages['troubleshootDevice_good'] },
        };
        const { color, label } = labels[value] || { color: 'green', label: messages['troubleshootDevice_good'] };
        return <span style={{ color }}>{label}</span>;
    }

    const handlerLocationValidation = (value) => {
        dispatch(editField({ field: "isLocation", value: value }))
        dispatch(editField({ field: "locationValidation", value: { error: value === "2", data: { isLocation: value === "1" } } }))
    }

    const handlerConfirmLastReading = (value) => {
        dispatch(editField({ field: "confirmLastReading", value: value }))
        dispatch(editField({ field: "locationValidation", value: { error: value === "2", data: { isLocation: value === "1" } } }))
    }

    // Effect to update data when lastReadings change
    useEffect(() => {
        if (lastReadings && lastReadings.latitude && lastReadings.longitude) {
            const newData = {
                lat: lastReadings.latitude,
                lng: lastReadings.longitude
            };
            setData(newData);
            setAddress(lastReadings.address);
            setHDop(lastReadings.hDop);
            dispatch(editField({ field: "locationValidation", value: { error: false, data: { isLocation: true } } }))
        }
    }, [lastReadings]);

    // Render function for GPS Signal and Address
    const renderLocationInfo = () => {
        if (loadingLastReading || loadingSendPing || loadingRebootVidfleetDevice || loadingRebootGeneralDevice) {
            return <Loading title={loadingSendPing ? messages['troubleshootDevice_refreshingLocation'] : (loadingRebootVidfleetDevice || loadingRebootGeneralDevice) ? messages['troubleshootDevice_resettingTheDevice'] : messages['troubleshootDevice_checkingLocation']} />;
        } else {
            return (
                <div style={{ width: '100%' }}>
                    <div style={{ display: 'grid' }}>
                        <span><span style={{ fontWeight: 600 }}>{messages['troubleshootDevice_gpsSignal']}</span>: {hDop ? hDopLabel(hDop) : messages['troubleshootDevice_noReading']}</span>
                        <span><span style={{ fontWeight: 600 }}>{messages['troubleshootDevice_address']}</span>: {address ? formatCatalogAddress(address) : messages['troubleshootDevice_noReading']}</span>
                    </div>
                    {
                        confirmLocation?.error ? (
                            <div style={{ padding: 10, textAlign: 'center' }}>
                                <div style={{ display: 'flex' }}>
                                    <Icon style={{ color: '#FF6624' }}>error</Icon>
                                    <p style={{ marginTop: 3 }}>
                                        {messages['troubleshootDevice_unitUnresponsive']} {' '}
                                        <IconButton
                                            onClick={(e) => {
                                                if (e) e.preventDefault();
                                                window.open(endpoints.TROUBLESHOOTING_URL, "_blank", "noopener,noreferrer")
                                            }}
                                            className="troubleshoot-device-icon"
                                            size="small"
                                            icon="open_in_new"
                                            style={{ color: '#00c8f8' }}
                                        />
                                    </p>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                                    <Tooltip
                                        title={supportChatAvailable
                                            ? messages["header_chatEnabled"]
                                            : messages["header_chatDisabeld"]}
                                    >
                                        <Button
                                            variant='primary'
                                            onClick={(e) => {
                                                if (e) e.preventDefault();
                                                openChat && openChat();
                                            }}
                                        >
                                            {messages['troubleshootDevice_supportChat']}
                                        </Button>
                                    </Tooltip>
                                </div>
                            </div>
                        ) : (
                            <>
                                {
                                    refreshLocation || unitDetail?.type?.toUpperCase() === "VIDFLEET" || unitDetail?.simCard?.simType === "USC" ? (
                                        <div>
                                            {
                                                openConfirmReset ? (
                                                    <div className="confirm-reset">
                                                        {
                                                            rebootVidfleetDevice || rebootGeneralDevice ? (
                                                                <div>
                                                                    {rebootVidfleetDevice?.result || (rebootGeneralDevice && rebootGeneralDevice?.result) ? (
                                                                        <div style={{ display: 'flex' }}>
                                                                            <Icon style={{ color: '#66BB6A' }}>check_circle</Icon>
                                                                            <p style={{ margin: '2px 9px' }}>{messages['troubleshootDevice_resetProcessHasBeenExecuted']}</p>
                                                                        </div>
                                                                    ) : (
                                                                        <div>
                                                                            <div style={{ display: 'flex' }}>
                                                                                <Icon style={{ color: '#FF6624' }}>error</Icon>
                                                                                <p style={{ margin: '2px 9px' }}>{messages['troubleshootDevice_restartProcessCouldNotBeExecuted']}</p>
                                                                            </div>
                                                                            <div>
                                                                                <p style={{ textAlign: 'center' }}>
                                                                                    {messages['troubleshootDevice_pleaseContactOurSupport']}
                                                                                    <IconButton
                                                                                        className="troubleshoot-device-icon"
                                                                                        onClick={(e) => {
                                                                                            if (e) e.preventDefault();
                                                                                            window.open(endpoints.TROUBLESHOOTING_URL, "_blank", "noopener,noreferrer")
                                                                                        }}
                                                                                        size="small"
                                                                                        icon="open_in_new"
                                                                                    />
                                                                                </p>
                                                                            </div>
                                                                            <div className="connection-history-error-button">
                                                                                <Tooltip
                                                                                    title={supportChatAvailable
                                                                                        ? messages["header_chatEnabled"]
                                                                                        : messages["header_chatDisabeld"]}
                                                                                >
                                                                                    <Button
                                                                                        variant='primary'
                                                                                        onClick={(e) => {
                                                                                            if (e) e.preventDefault();
                                                                                            openChat && openChat();
                                                                                        }}
                                                                                    >
                                                                                        {messages['troubleshootDevice_supportChat']}
                                                                                    </Button>
                                                                                </Tooltip>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <p>
                                                                        {messages['troubleshootDevice_resetDeviceMessage']}
                                                                        <strong> {messages['troubleshootDevice_resetDeviceConfirm']}</strong>
                                                                    </p>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 15 }}>
                                                                        <Button
                                                                            onClick={(e) => {
                                                                                if (e) e.preventDefault();
                                                                                setOpenConfirmReset(false)
                                                                            }}
                                                                            variant='secondary'
                                                                        >
                                                                            {messages['troubleshootDevice_back']}
                                                                        </Button>
                                                                        <Button
                                                                            onClick={(e) => {
                                                                                if (e) e.preventDefault();
                                                                                resetDevice()
                                                                            }}
                                                                            variant='primary'
                                                                        >
                                                                            {messages['troubleshootDevice_Reset']}
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                ) : (
                                                    <div style={{ textAlign: 'center' }}>
                                                        {
                                                            lastReadings && !lastReadings?.error && (
                                                                <>
                                                                    <p style={{ marginBottom: 0 }}>
                                                                        {
                                                                            unitDetail?.type?.toUpperCase() === "VIDFLEET" || unitDetail?.simCard?.simType === "USC" ? messages['troubleshootDevice_IsThisWhereTheUnitIsLocated'] : messages['troubleshootDevice_unitResponsive']
                                                                        }
                                                                    </p>
                                                                    <div className="verify-location-options">
                                                                        <RadioGroup
                                                                            onChange={(e) => {
                                                                                handlerConfirmLastReading(e)
                                                                            }}
                                                                            value={confirmLastReading}
                                                                            list={options}
                                                                            labelDir="row"
                                                                        />
                                                                    </div>
                                                                </>
                                                            )
                                                        }

                                                        <div style={{ display: 'flex', justifyContent: 'center' }}>

                                                            {
                                                                locationValidation?.error || lastReadings?.error ? (
                                                                    <>
                                                                        {
                                                                            unitDetail?.simCard?.simType === "USC" ? (
                                                                                <div>
                                                                                    <div>
                                                                                        <p style={{ textAlign: 'center' }}>
                                                                                            {messages['troubleshootDevice_pleaseContactOurSupport']}
                                                                                            <IconButton
                                                                                                className="troubleshoot-device-icon"
                                                                                                onClick={(e) => {
                                                                                                    if (e) e.preventDefault();
                                                                                                    window.open(endpoints.TROUBLESHOOTING_URL, "_blank", "noopener,noreferrer")
                                                                                                }}
                                                                                                size="small"
                                                                                                icon="open_in_new"
                                                                                            />
                                                                                        </p>
                                                                                    </div>
                                                                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                                                                                        <Tooltip
                                                                                            title={supportChatAvailable
                                                                                                ? messages["header_chatEnabled"]
                                                                                                : messages["header_chatDisabeld"]}
                                                                                        >
                                                                                            <Button
                                                                                                variant='primary'
                                                                                                onClick={(e) => {
                                                                                                    if (e) e.preventDefault();
                                                                                                    openChat && openChat();
                                                                                                }}
                                                                                            >
                                                                                                {messages['troubleshootDevice_supportChat']}
                                                                                            </Button>
                                                                                        </Tooltip>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <div>
                                                                                    <p>{messages['troubleshootDevice_resetDeviceResponse']}</p>
                                                                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 15 }}>
                                                                                        <Button
                                                                                            onClick={(e) => {
                                                                                                if (e) e.preventDefault();
                                                                                                setOpenConfirmReset(true);
                                                                                            }}
                                                                                            variant='primary'
                                                                                        >
                                                                                            {messages['troubleshootDevice_Reset']}
                                                                                        </Button>
                                                                                    </div>
                                                                                </div>
                                                                            )
                                                                        }
                                                                    </>

                                                                ) : (
                                                                    <div>
                                                                        <p style={{ marginBottom: 0 }}>{messages['troubleshootDevice_theLocationIsAccurate']}</p>
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        </div>
                                    ) : (
                                        <div style={{ padding: 10, textAlign: 'center' }}>
                                            <p style={{ marginBottom: 0 }}>{messages['troubleshootDevice_IsThisWhereTheUnitIsLocated']}</p>
                                            <div className="verify-location-options">
                                                <RadioGroup
                                                    onChange={(e) => handlerLocationValidation(e)}
                                                    value={isLocation}
                                                    list={options}
                                                    labelDir="row"
                                                />
                                            </div>
                                            {
                                                locationValidation?.error && (
                                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <p style={{ marginBottom: 0 }}>{messages['troubleshootDevice_clickPingBuntton']}</p>
                                                    </div>
                                                )
                                            }
                                        </div>
                                    )
                                }
                            </>
                        )
                    }
                </div>
            );
        }
    };

    return (
        <div style={{ width: '100%' }}>
            <Map
                coords={data}
                zoom={15}
            />
            <div
                className="troubleshoot-device-main"
                style={{
                    marginTop: 10,
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                {renderLocationInfo()}
            </div>
        </div>
    )
}

export default VerifyLocation